// -- == //
import React from 'react';
import ImageUploading, { ImageListType } from 'react-images-uploading';

import { Req } from '../base';
import * as S from './styles';
import * as T from 'common/types';

interface IProps {
  label?: string;
  disabled?: boolean | undefined;
  onChange?: (e: T.Form.FileUpload) => void;
  style?: object;
  id?: string;
  placeholder?: string;
  error?: boolean;
  success?: boolean;
  message?: string | boolean;
  type?: string;
  name?: string;
  onBlur?: (e: object) => void;
  defaultImage?: string | null;
  icon?: string;
  inputStyle?: object;
  accept?: string[];
  value?: object | null;
  labelStyle?: object;
  /** The shape the image should be — the design's `.ratio`, set in mono. */
  ratio?: string;
  /** Renders the design's required asterisk after the label. */
  required?: boolean;
}

/*
 * Inline rather than a `<ReactSVG>` fetch of `/icons/stroke/image-plus.svg`
 * painted `#A4A6B3`: it inherits `currentColor`, so the zone's hover state
 * carries the icon with it.
 */
const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10" r="2" />
    <path d="M21 16l-5-5-6 6" />
  </svg>
);

const UploadBox: React.FC<IProps> = ({
  label,
  value,
  labelStyle,
  style,
  error,
  success,
  message,
  onChange,
  defaultImage,
  placeholder,
  ratio,
  required,
  accept = ['png', 'jpg', 'jpeg', 'webp']
}) => {
  const onChangeFile = (imageList: ImageListType) => {
    if (onChange) onChange(imageList[0] as unknown as T.Form.FileUpload);
  };

  return (
    <S.inputContainer style={style} className={error ? 'error' : success ? 'success' : ''}>
      <ImageUploading value={[{ ...value }]} onChange={onChangeFile} maxNumber={1} acceptType={accept}>
        {({ imageList, onImageUpload, onImageUpdate, isDragging, dragProps }) => {
          const preview = imageList[0]?.dataURL || defaultImage;

          return (
            <>
              {label ? (
                <S.label className="input-label" style={labelStyle}>
                  {label}
                  {required ? <Req aria-hidden="true">*</Req> : <></>}
                </S.label>
              ) : (
                <></>
              )}
              <S.InputBox
                onClick={() => {
                  imageList[0] ? onImageUpdate(0) : onImageUpload();
                }}
                className={`${isDragging ? 'dragging' : ''} ${preview ? 'filled' : ''}`.trim()}
                {...dragProps}
              >
                {preview ? (
                  <S.img src={preview as string} className="icon" alt="" />
                ) : (
                  <>
                    <S.Icon>
                      <ImageIcon />
                    </S.Icon>
                    {placeholder ? <S.Text>{placeholder}</S.Text> : <></>}
                    {ratio ? <S.Ratio>{ratio}</S.Ratio> : <></>}
                  </>
                )}
              </S.InputBox>
              {/* The zone stops saying what it does once it holds a picture,
                  so the way to change it goes underneath. */}
              {preview ? <S.Hint>Click the image to replace it.</S.Hint> : <></>}
            </>
          );
        }}
      </ImageUploading>

      {message ? (
        <S.message className={error ? 'error-message' : success ? 'success-message' : ''}>{message}</S.message>
      ) : (
        <></>
      )}
    </S.inputContainer>
  );
};

export default UploadBox;
